import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, ChevronDown, X } from "lucide-react";
import { useLang } from "../LangContext";
import { LANG_EMOJI } from "../i18n";

/**
 * Selettore lingua: bandiere + etichette.
 * Su mobile apre un pannello fixed (portal) per non essere tagliato da overflow:clip.
 * @param {"default"|"header"} variant — header = sfondo chiaro per barra bianca
 */
export default function LangSwitcher({ variant = "default" }) {
  const { lang, setLang, langs } = useLang();
  const [open, setOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const isHeader = variant === "header";

  const current = langs.find((l) => l.code === lang) || langs[0];
  const currentEmoji = current?.emoji || LANG_EMOJI[lang] || "🌐";

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 900px)");
    const sync = () => setIsNarrow(!!mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    if (isNarrow) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isNarrow]);

  function pickLang(code) {
    setLang(code);
    setOpen(false);
    try {
      localStorage.setItem("6amico_lang_explicit", "1");
      localStorage.setItem("6amico_lang", code);
    } catch {
      /* ignore */
    }
  }

  if (!langs.length) {
    return (
      <span
        className={
          isHeader
            ? "sc-selector-or-actions-btn inline-flex items-center gap-1.5 text-xs font-mono uppercase text-[#1f2937] border border-[#d1d5db] rounded-md px-2.5 py-1.5 bg-white"
            : "inline-flex items-center gap-1.5 text-xs font-mono uppercase text-slate-400 border border-slate-700 rounded-md px-2.5 py-1.5"
        }
        data-testid="lang-badge"
      >
        <Globe size={14} aria-hidden />
        {lang}
      </span>
    );
  }

  const triggerClass = isHeader
    ? "sc-selector-or-actions-btn inline-flex items-center gap-1.5 text-sm font-medium text-[#1f2937] border border-[#d1d5db] rounded-md px-2.5 py-1.5 bg-white hover:bg-[#f9fafb] transition-colors"
    : "inline-flex items-center gap-1.5 text-sm font-medium text-slate-200 hover:text-white border border-slate-700 rounded-md px-2.5 py-1.5 bg-slate-900/80 transition-colors";

  const optionActiveHeader =
    "flex items-center gap-3 rounded-xl px-3 py-3 text-left bg-[#eff6ff] border border-[#0066cc] text-[#0066cc]";
  const optionIdleHeader =
    "flex items-center gap-3 rounded-xl px-3 py-3 text-left border border-[#e5e7eb] text-[#1f2937] bg-white hover:bg-[#f3f4f6]";
  const optionActiveDark =
    "flex items-center gap-3 rounded-xl px-3 py-3 text-left bg-amber-900/35 border border-amber-800/50 text-amber-100";
  const optionIdleDark =
    "flex items-center gap-3 rounded-xl px-3 py-3 text-left border border-transparent text-slate-200 hover:bg-slate-800/80";

  const langButtons = (
    <div
      className={isNarrow ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-1.5"}
      role="none"
    >
      {langs.map((l) => {
        const active = lang === l.code;
        const optionClass = isHeader
          ? active
            ? optionActiveHeader
            : optionIdleHeader
          : active
            ? optionActiveDark
            : optionIdleDark;

        return (
          <button
            key={l.code}
            type="button"
            role="option"
            aria-selected={active}
            data-testid={`lang-option-${l.code}`}
            onClick={() => pickLang(l.code)}
            className={`${optionClass} transition-colors min-h-[48px]`}
          >
            <span className="text-2xl leading-none shrink-0" aria-hidden>
              {l.emoji || LANG_EMOJI[l.code]}
            </span>
            <span className="text-sm font-semibold leading-tight">{l.label}</span>
          </button>
        );
      })}
    </div>
  );

  const mobileSheet =
    open &&
    isNarrow &&
    typeof document !== "undefined" &&
    createPortal(
      <div className="fixed inset-0 z-[10050] notranslate" data-testid="lang-menu-portal" translate="no">
        <button
          type="button"
          className="absolute inset-0 bg-black/45 border-0 cursor-pointer"
          aria-label="Chiudi lingue"
          onClick={() => setOpen(false)}
        />
        <div
          role="listbox"
          aria-label="Scegli lingua"
          data-testid="lang-menu"
          className="absolute left-0 right-0 bottom-0 max-h-[min(85dvh,32rem)] overflow-y-auto rounded-t-2xl bg-white shadow-2xl border border-[#e5e7eb] p-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-mono uppercase tracking-widest text-[#6b7280] m-0">
              Lingua / Language
            </p>
            <button
              type="button"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#e5e7eb] text-[#374151]"
              aria-label="Chiudi"
              onClick={() => setOpen(false)}
            >
              <X size={18} />
            </button>
          </div>
          {langButtons}
        </div>
      </div>,
      document.body,
    );

  const desktopMenu =
    open &&
    !isNarrow && (
      <>
        <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
        <div
          role="listbox"
          aria-label="Scegli lingua"
          className={
            isHeader
              ? "absolute right-0 mt-2 z-50 w-[16rem] max-h-[min(70vh,24rem)] overflow-y-auto rounded-lg border border-[#d1d5db] bg-white shadow-xl p-2"
              : "absolute right-0 mt-2 z-50 w-[16rem] max-h-[min(70vh,24rem)] overflow-y-auto rounded-lg border border-amber-900/35 bg-slate-950 shadow-xl p-2"
          }
          data-testid="lang-menu"
        >
          <p
            className={
              isHeader
                ? "px-2 pb-2 text-[10px] font-mono uppercase tracking-widest text-[#6b7280]"
                : "px-2 pb-2 text-[10px] font-mono uppercase tracking-widest text-slate-500"
            }
          >
            Lingua
          </p>
          {langButtons}
        </div>
      </>
    );

  return (
    <div className="relative notranslate" data-testid="lang-switcher-wrap" translate="no">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={triggerClass}
        data-testid="lang-switcher"
        title="Lingua dell'interfaccia"
      >
        <span className="text-base leading-none" aria-hidden title={current?.label || lang}>
          {currentEmoji}
        </span>
        <span className="sr-only">{current?.label || lang}</span>
        <ChevronDown size={14} className={`opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {mobileSheet}
      {desktopMenu}
    </div>
  );
}
